/* The agency, if any, that brought this person to the college.

   The student page used to show a bare agency picker buried in Enrollment
   while the admissions page showed the agent's full contact details. Both now
   render this, so the same facts appear in the same place on both.

   The agency link itself and the typed contact details are different things:
   the link points at an agency record the college manages, and the contact
   details are what the applicant typed on the form. A record can have either
   without the other, which is why both are shown. */

import { Building2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EditableField } from '@/components/admin/ProfileField';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import AgencyCombobox from '@/components/admin/AgencyCombobox';
import {
  inputClass,
  sectionCardClass,
  sectionTitleClass,
  sectionTitleStyle,
} from './controls';

export default function AgencySection({
  person,
  form,
  editing = false,
  setField,
  agentOptions = [],
  onSelectAgency,
  // Resolved from the linked agency record, which is authoritative for the
  // legal name -- what the applicant typed is only ever what they typed.
  linkedAgencyName = '',
  // The student page's old agency row linked through to the agency record.
  // Worth keeping, and worth both pages having.
  linkedAgencyHref = '',
  linkedAgencyLegalName = '',
  linkedAgencyPreferredName = '',
  /* Records saved before an agency was linked have blank agent fields. Reading
     through to the agency's own contact details shows who the agent actually
     is rather than a row of dashes. Only blanks fall through, so a detail
     someone corrected by hand is never replaced by the agency's version. */
  agentFallback = null,
  // Some pages have no agency contact details to show at all (a student record
  // stores the link but not the agent's phone or email).
  showAgentContact = true,
  title = 'Agency',
}) {
  const view = person || {};
  const draft = form || view;
  const setText = (key) => (event) => setField?.(key, event.target.value);

  const hasAgency = editing ? Boolean(draft.usingAgency) : Boolean(view.usingAgency || view.agentId);
  // A brand or preferred name is only worth showing beside the legal name when
  // it actually differs, and "Bright Futures" is not a different name from
  // "BRIGHT FUTURES".
  const showPreferredName = linkedAgencyPreferredName
    && linkedAgencyPreferredName.toLocaleLowerCase('en-CA')
      !== String(linkedAgencyLegalName || '').trim().toLocaleLowerCase('en-CA');

  const detail = (key) => String(view[key] || '').trim() || String(agentFallback?.[key] || '').trim();

  return (
    <Card className={sectionCardClass}>
      <CardHeader className="pb-3">
        <CardTitle className={sectionTitleClass} style={sectionTitleStyle}>
          <Building2 className="w-4 h-4 text-[#D4AF37]" /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {editing && <p className="text-xs text-[#062B1A]/60">Saving agency changes updates this person's linked student and admissions profiles. Locked admissions records keep their saved snapshot. Selecting a different agency replaces the contact details below.</p>}

        {editing && (
          <div className="flex items-center gap-3">
            <Switch
              aria-label="Using an agency"
              checked={Boolean(draft.usingAgency)}
              onCheckedChange={(checked) => setField?.('usingAgency', checked)}
            />
            <span className="text-sm text-[#062B1A]">Using an agency</span>
          </div>
        )}

        {hasAgency ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Only offered while editing: read-only it duplicated the legal
                name directly below it. */}
            {editing && (
              <EditableField label="Agency" editing display={linkedAgencyName || 'Not set'} full>
                <AgencyCombobox
                  options={agentOptions}
                  value={draft.agentId || ''}
                  onChange={(id) => onSelectAgency?.(id)}
                  className={inputClass}
                />
              </EditableField>
            )}
            <EditableField
              label="Agency/Company legal name"
              editing={editing && showAgentContact && !draft.agentId}
              display={(linkedAgencyLegalName || detail('agencyCompanyName')) ? (
                <>
                  {linkedAgencyHref ? (
                    <a
                      href={linkedAgencyHref}
                      className="underline decoration-[#D4AF37]/60 underline-offset-2 hover:text-[#D4AF37]"
                    >
                      {linkedAgencyLegalName || detail('agencyCompanyName')}
                    </a>
                  ) : (linkedAgencyLegalName || detail('agencyCompanyName'))}
                  {showPreferredName && (
                    <span className="block mt-1 text-xs font-normal text-[#062B1A]/45">
                      {linkedAgencyPreferredName}
                    </span>
                  )}
                </>
              ) : ''}
              full
            >
              <Input
                value={draft.agencyCompanyName || ''}
                onChange={setText('agencyCompanyName')}
                className={inputClass}
              />
            </EditableField>
            {showAgentContact && (
              <>
                <EditableField label="Agent first name" editing={editing} display={detail('agentFirstName')}>
                  <Input value={draft.agentFirstName || ''} onChange={setText('agentFirstName')} className={inputClass} />
                </EditableField>
                <EditableField label="Agent last name" editing={editing} display={detail('agentLastName')}>
                  <Input value={draft.agentLastName || ''} onChange={setText('agentLastName')} className={inputClass} />
                </EditableField>
                <EditableField label="Agent phone" editing={editing} display={detail('agentPhone')}>
                  <Input value={draft.agentPhone || ''} onChange={setText('agentPhone')} className={inputClass} />
                </EditableField>
                <EditableField label="Agent email" editing={editing} display={detail('agentEmail')}>
                  <Input value={draft.agentEmail || ''} onChange={setText('agentEmail')} className={inputClass} />
                </EditableField>
                <EditableField label="Agency notes" editing={editing} display={view.agencyNotes} full>
                  <Textarea
                    value={draft.agencyNotes || ''}
                    onChange={setText('agencyNotes')}
                    className={inputClass}
                    rows={3}
                  />
                </EditableField>
              </>
            )}
          </div>
        ) : (
          <p className="text-sm text-[#062B1A]/55">No agency — direct applicant.</p>
        )}
      </CardContent>
    </Card>
  );
}
